// Who a course is for, as two groups set on Details:
//  - Read & Assess (required): its roles read and are assessed on the lessons
//    for their own capabilities (and on lessons that build none), and read the
//    rest.
//  - Read Only (optional): reads every lesson, to stay in the loop.
// Each group holds one or more audiences. An audience is a set of picks: roles,
// and values of the organization's other lists; any value within a list, every
// list it picks from. A list it leaves alone means anyone.
//
// Nobody may be in both groups: Details won't go on while a Read & Assess
// audience and a Read Only audience can both match someone (overlaps()).
// No React here, so it can be tested on its own.
//
// The backend keeps them on the program (audienceGroups) once
// audienceRequirementsBackend() is on (ProgramSettings/requirements/flags.ts):
// the build flow sends them on create and on the Details save, and reads them
// back from GET programs/{id}/audienceRequirements (toServerGroups,
// fromServerGroups). The backend runs the same overlap check (409).
//
// DUMMY, until that flag flips: they live in Details (FlowDetails.groups) and
// in this browser per program, for the build flow to reopen and the designer's
// Audience Requirements to read. The build snapshot saved on the server leaves
// them out (trainingSnapshot.ts).

import type { ProgramAudienceGroups } from '../../../../../api/audienceRequirements';

export interface AudienceDraft {
  id: string;
  /** What the admin calls it ("NBD New Hires"); blank, it is called by its picks. */
  name?: string;
  /** Roles from Capability Setup; none means any role (Read Only only). */
  roleIds: string[];
  /** Values picked, by list id; a list left out means anyone. */
  picks: Record<string, string[]>;
}

export interface AudienceGroupsDraft {
  readAssess: AudienceDraft[];
  readOnly: AudienceDraft[];
}

let seq = 0;
export const newAudience = (): AudienceDraft => ({
  id: `aud-${Date.now()}-${seq++}`,
  name: '',
  roleIds: [],
  picks: {}
});

/** No audience group yet in either group: each is added through the modal. */
export const emptyGroups = (): AudienceGroupsDraft => ({ readAssess: [], readOnly: [] });

/** Every role either group names: the program's roleIds (the backend derives the same from the groups). */
export const allRoleIds = (g: AudienceGroupsDraft): string[] =>
  Array.from(new Set([...g.readAssess, ...g.readOnly].flatMap((a) => a.roleIds)));

/** The Read & Assess roles: the ones the course is built for, whose capabilities it builds (Read Only only reads). */
export const readAssessRoleIds = (g: AudienceGroupsDraft): string[] =>
  Array.from(new Set(g.readAssess.flatMap((a) => a.roleIds)));

/**
 * The groups as the backend takes them (POST group/create, PATCH programs/{id}):
 * an audience group with nothing picked yet matches nobody and is left out, as
 * Details leaves it out, and a name is sent trimmed.
 */
export const toServerGroups = (g: AudienceGroupsDraft): ProgramAudienceGroups => {
  const sent = (list: AudienceDraft[]) =>
    list
      .filter((a) => !isBlank(a))
      .map((a) => ({
        id: a.id,
        name: (a.name ?? '').trim(),
        roleIds: [...a.roleIds],
        picks: Object.fromEntries(Object.entries(a.picks).filter(([, values]) => values.length > 0))
      }));
  return { readAssess: sent(g.readAssess), readOnly: sent(g.readOnly) };
};

/** The groups a program holds (GET programs/{id}/audienceRequirements), as Details edits them. */
export const fromServerGroups = (g: ProgramAudienceGroups): AudienceGroupsDraft => {
  const drafts = (list: ProgramAudienceGroups['readAssess'] | undefined) =>
    (list ?? []).map((a) => ({
      id: String(a.id),
      name: a.name ?? '',
      roleIds: (a.roleIds ?? []).map(String),
      picks: Object.fromEntries(Object.entries(a.picks ?? {}).map(([list, values]) => [list, values.map(String)]))
    }));
  return { readAssess: drafts(g.readAssess), readOnly: drafts(g.readOnly) };
};

const ROLE = 'role';
const picksOf = (a: AudienceDraft): Record<string, string[]> => ({ [ROLE]: a.roleIds, ...a.picks });

/**
 * What two audiences both match, list by list; null when they can't both match anyone.
 *
 * A list either one picks from separates them when their picks there share no value. That is exact
 * while a person holds one value per list. Someone holding two (roles in two teams, PRD rule 8, or
 * two values of another list) can still match both through different values; this check can't see
 * that, so the backend's rule 8 backstop (Read & Assess wins) is what settles it.
 */
const shared = (a: AudienceDraft, b: AudienceDraft): Record<string, string[]> | null => {
  const pa = picksOf(a);
  const pb = picksOf(b);
  const out: Record<string, string[]> = {};
  for (const list of new Set([...Object.keys(pa), ...Object.keys(pb)])) {
    const x = pa[list]?.length ? pa[list] : null;
    const y = pb[list]?.length ? pb[list] : null;
    if (!x && !y) continue;
    const both = !x ? y! : !y ? x : x.filter((v) => y.includes(v));
    if (both.length === 0) return null;
    out[list] = both;
  }
  return out;
};

export interface Overlap {
  readAssess: AudienceDraft;
  readOnly: AudienceDraft;
  /** Who would be in both, as picks (roles under 'role'); a list neither picks from is left out (anyone). */
  who: Record<string, string[]>;
}

/** Every Read & Assess audience against every Read Only one: who could be in both. */
export const overlaps = (g: AudienceGroupsDraft): Overlap[] =>
  g.readAssess.flatMap((ra) =>
    g.readOnly.flatMap((ro) => {
      // An audience with nothing picked yet matches nobody.
      if (isBlank(ra) || isBlank(ro)) return [];
      const who = shared(ra, ro);
      return who ? [{ readAssess: ra, readOnly: ro, who }] : [];
    })
  );

export const isBlank = (a: AudienceDraft): boolean =>
  a.roleIds.length === 0 && Object.values(a.picks).every((v) => v.length === 0);

/** What still stops Details going on, for the step's bar. */
export const groupsMissing = (g: AudienceGroupsDraft): string[] =>
  [
    !g.readAssess.some((a) => a.roleIds.length > 0) && 'Read & Assess roles',
    g.readAssess.some((a) => !isBlank(a) && a.roleIds.length === 0) && 'A role in every Read & Assess audience',
    overlaps(g).length > 0 && 'Separate Read & Assess and Read Only'
  ].filter((m): m is string => Boolean(m));

/** An audience as words: "Account Exec · New Hire · London". */
export const describe = (
  picks: Record<string, string[]>,
  roleName: (id: string) => string,
  valueName: (id: string) => string,
  listOrder: string[]
): string => {
  const parts: string[] = [];
  const roles = picks[ROLE];
  if (roles?.length) parts.push(roles.map(roleName).join(' & '));
  for (const list of listOrder) {
    const vals = picks[list];
    if (vals?.length) parts.push(vals.map(valueName).join(' & '));
  }
  return parts.join(' · ') || 'Anyone';
};

/** Its picks as words: "Account Exec · New Hire · London". */
export const audienceDescription = (
  a: AudienceDraft,
  roleName: (id: string) => string,
  valueName: (id: string) => string,
  listOrder: string[]
): string => describe(picksOf(a), roleName, valueName, listOrder);

/** What it is called: the admin's name, else its picks. */
export const audienceName = (
  a: AudienceDraft,
  roleName: (id: string) => string,
  valueName: (id: string) => string,
  listOrder: string[]
): string => a.name?.trim() || audienceDescription(a, roleName, valueName, listOrder);

/** Its picks list by list, for a tooltip: Roles, then each list it narrows by. */
export const audienceParts = (
  a: AudienceDraft,
  roleName: (id: string) => string,
  valueName: (id: string) => string,
  lists: Array<{ _id: string; name: string }>
): Array<{ label: string; values: string[] }> => [
  ...(a.roleIds.length ? [{ label: 'Roles', values: a.roleIds.map(roleName) }] : []),
  ...lists
    .filter((l) => (a.picks[l._id] ?? []).length > 0)
    .map((l) => ({ label: l.name, values: a.picks[l._id].map(valueName) }))
];

// ── DUMMY: kept in this browser per program, while audienceRequirementsBackend() is off ──

const key = (grpId: string) => `dosen.audienceGroups.${grpId}`;

export const saveGroups = (grpId: string, g: AudienceGroupsDraft): void => {
  try {
    localStorage.setItem(key(grpId), JSON.stringify(g));
  } catch {
    // Private window or full storage: the designer falls back to its example groups.
  }
};

export const loadGroups = (grpId: string): AudienceGroupsDraft | null => {
  try {
    const raw = localStorage.getItem(key(grpId));
    return raw ? (JSON.parse(raw) as AudienceGroupsDraft) : null;
  } catch {
    return null;
  }
};
